import React, { useRef } from 'react';
import { Box, TextField, Button, useMediaQuery, useTheme } from '@mui/material';
import { Formik } from 'formik';
import * as yup from 'yup';
import Header from '../global/Header';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useAddNewBlogPostMutation } from '../../../features/blog_posts/blogPostsApiSlice';

const initialValues = {
  title: '',
  content: '',
  author: ''
};

const blogPostSchema = yup.object().shape({
  title: yup.string().required('Title is required'),
  author: yup.string().required('Author is required'),
  content: yup.string().required('Content is required'),
});

const DevBlogForm = ({ onSubmit }) => {
  const [addNewBlogPost] = useAddNewBlogPostMutation();
  const theme = useTheme();
  const isNonMobile = useMediaQuery('(min-width:600px');

  const quillRef = useRef(null);

  const quillToolbar = [
  ['bold', 'italic', 'underline', 'strike'],
  ['blockquote', 'code-block'],
  [{ header: 1 }, { header: 2 }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ script: 'sub' }, { script: 'super' }],
  [{ indent: '-1' }, { indent: '+1' }],
  [{ direction: 'rtl' }],
  [{ size: ['small', false, 'large', 'huge'] }],
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  [{ color: [] }, { background: [] }],
  [{ font: [] }],
  [{ align: [] }],
  ['clean'],
  ['link'],
  ['insertImageFromURL'],
];

  const insertImageFromURL = () => {
    const quill = quillRef.current.getEditor();
    const range = quill.getSelection();
    const imageURL = prompt("Please enter the image URL:");
    if (imageURL) {
      quill.insertEmbed(range.index, "image", imageURL);
    }
  };

  const toolbarModule = Quill.import('modules/toolbar');
  toolbarModule.DEFAULTS.handlers.insertImageFromURL = insertImageFromURL;

  const handleSubmitForm = async (values, { setSubmitting }) => {
    try {
      await addNewBlogPost({ title: values.title, author: values.author, content: values.content }).unwrap();
      // Handle success, e.g., show a success message or navigate to another page.
    } catch (error) {
      // Handle error, e.g., show an error message.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Header title="Create a New Blog Post" />
      <Formik onSubmit={handleSubmitForm} initialValues={initialValues} validationSchema={blogPostSchema}>
        {({ values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              sx={{
                '& > div': { gridColumn: isNonMobile ? undefined : 'span 4' },
              }}
            >
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Title"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.title}
                name="title"
                error={!!touched.title && !!errors.title}
                helperText={touched.title && errors.title}
                sx={{ gridColumn: 'span 4' }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Author"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.author}
                name="author"
                error={!!touched.author && !!errors.author}
                helperText={touched.author && errors.author}
                sx={{ gridColumn: 'span 4' }}
              />
              <ReactQuill
                ref={quillRef}
                value={values.content}
                onChange={(content) => setFieldValue('content', content)}
                theme="snow"
                style={{ gridColumn: 'span 4' }}
                modules={{
                  toolbar: quillToolbar,
                }}
              />
            </Box>
            <Box display="flex" justifyContent="end" mt="20px">
              <Button type="submit" color="secondary" variant="contained">
                Create New Blog Post
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </>
  );
};

export default DevBlogForm;